import { Icon } from '@gftg/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/** DATA-06 in the UI: an empty or failed screen still says something useful, never an error blob. */
export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <p role="status" className="px-gutter text-body py-8 text-center text-ink-3">
      {label}
    </p>
  );
}

/**
 * `onRetry` is offered only where trying again can change the answer — a dropped request in a
 * ballroom. A session that is not in the published program is not a connection problem, and a
 * button that reruns it would only ask the attendee to fail twice.
 */
export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="mx-gutter rounded-card text-body my-6 border border-alert-border bg-alert-bg p-4 text-alert-text">
      <p className="font-semibold">{message}</p>
      <p className="mt-1">
        {onRetry
          ? 'Check your connection and try again. If it keeps happening, tell the organizers.'
          : 'If you followed a link, it may be out of date. Tell the organizers if it should work.'}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-button rounded-button mt-3 flex min-h-touch items-center justify-center border border-alert-border bg-surface px-4 font-semibold text-alert-text"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyBlock({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mx-gutter rounded-card my-6 border border-dashed border-border-dashed bg-surface p-5 text-center">
      <p className="text-row font-semibold text-ink">{title}</p>
      {children && <p className="text-body mt-1 text-ink-3">{children}</p>}
    </div>
  );
}

/**
 * The screen header from the design: a round back button, the title beside it, and room for one
 * action on the right. Even with the tab bar there is a way back, because a tab is not an
 * "undo" — the agenda's own filters and the session you came from are not tabs.
 */
export function PageHeader({
  title,
  subtitle,
  backTo = '/',
  backLabel = 'Back',
  action,
}: {
  title: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  action?: ReactNode;
}) {
  return (
    <header className="px-gutter pt-3 pb-4">
      <div className="flex items-center gap-3">
        <Link to={backTo} aria-label={backLabel} className={ICON_BUTTON}>
          <Icon name="chevron-left" size={18} />
        </Link>
        <h1 className="font-display text-screen-title min-w-0 flex-1 truncate font-semibold text-ink">
          {title}
        </h1>
        {action}
      </div>
      {subtitle && <p className="text-subtitle mt-2 text-ink-3">{subtitle}</p>}
    </header>
  );
}

/** 36px, the kit's floor for an icon button, on the neutral well it specifies. */
export const ICON_BUTTON =
  'flex size-tap shrink-0 items-center justify-center rounded-full bg-sand-2 text-ink-2';

/** A row inside a divided card — the home screen's conference list and the profile's settings. */
export const LIST_ROW = 'flex min-h-row items-center gap-3 px-4 py-3 text-left';
